import { useContext, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import type { AutocompleteInputChangeReason } from '@mui/material/useAutocomplete';
import { APIContext } from '../../Contexts/APIContext';
import { SessionContext } from '../../Contexts/SessionContext';
import LandUseViewModel from '../ViewModels/LandUseViewModel';
import { SessionState } from '../../Contexts/SessionState';
import Box from '@mui/material/Box';

interface IProps {
    area: 'SiteList' | 'TripRate';
    getData?: (newState?: SessionState) => void;
}

const getLandUseCode = (landUse: LandUseViewModel) =>
    `${landUse.mainLandUseCode}/${landUse.subLandUseCode}`;

const getLandUseOptionLabel = (landUse: LandUseViewModel) =>
    `${getLandUseCode(landUse)} - ${landUse.subLandUse}`;

const filterLandUseOptions = (
    options: LandUseViewModel[],
    inputValue: string
) => {
    const search = inputValue.trim().toLowerCase();
    if (!search) {
        return options;
    }

    const isMainOrFullCode = /^\d{2}(?:\/[a-z0-9]+)?$/i.test(search);
    const isSubCode = /^[a-z]$/i.test(search);

    return options.filter((landUse) => {
        if (isMainOrFullCode) {
            return getLandUseCode(landUse).toLowerCase().startsWith(search);
        }

        if (isSubCode) {
            return landUse.subLandUseCode.toLowerCase() === search;
        }

        return [
            getLandUseOptionLabel(landUse),
            landUse.mainLandUse,
            landUse.subLandUse,
        ].some((value) => value.toLowerCase().includes(search));
    });
};

export default function LanduseSelection({ area, getData }: IProps) {
    const { get, loading } = useContext(APIContext);
    const { sessionState, updateSessionState } = useContext(SessionContext);
    const [landUseDropDown, setLandUseDropDown] = useState<LandUseViewModel[]>([]);
    const tableKey = area === 'SiteList' ? 'siteList' : 'summaryFiltering';
    const selectedTripRateLandUse = sessionState.tripRateSearch
        ? landUseDropDown.find((landUse) =>
            landUse.archived === false &&
            landUse.mainLandUseCode === sessionState.tripRateMain &&
            landUse.subLandUseCode === sessionState.tripRateSub
        ) ?? null
        : null;
    const selectedSiteListLandUse = sessionState.siteListLandSearch
        ? landUseDropDown.find((landUse) =>
            landUse.archived === false &&
            landUse.mainLandUseCode === sessionState.siteListMain &&
            landUse.subLandUseCode === sessionState.siteListSub
        ) ?? null
        : null;

    useEffect(() => {
        get("SiteList", `GetLandUseDropDown`, setLandUseDropDown);
    }, [])

    const handleSiteListLandUseSearch = (_: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
        if (reason !== 'input' && reason !== 'clear') {
            return;
        }

        const nextTableState = {
            ...sessionState.siteListTableState,
            [tableKey]: {
                ...sessionState.siteListTableState[tableKey],
                page: 0,
            },
        };
        updateSessionState({
            siteListLandSearch: value,
            siteListPage: 0,
            siteListTableState: nextTableState
        }, (newState) => {
            if (reason === 'clear' && getData) {
                getData(newState);
            }
        });
    }

    const handleSiteListLandUseSelect = (_: React.SyntheticEvent, landUse: LandUseViewModel | null) => {
        if (!landUse) {
            return;
        }

        const nextTableState = {
            ...sessionState.siteListTableState,
            siteList: {
                ...sessionState.siteListTableState.siteList,
                page: 0,
            },
        };

        updateSessionState({
            siteListMain: landUse.mainLandUseCode,
            siteListSub: landUse.subLandUseCode,
            siteListLandSearch: getLandUseOptionLabel(landUse),
            siteListPage: 0,
            siteListTableState: nextTableState,
        }, getData);
    };

    const handleTripRateLandUseSearch = (_: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
        if (reason !== 'input' && reason !== 'clear') {
            return;
        }

        updateSessionState({
            tripRateSearch: value,
        });
    };

    const handleTripRateLandUseSelect = (_: React.SyntheticEvent, landUse: LandUseViewModel | null) => {
        if (!landUse) {
            return;
        }

        updateSessionState({
            tripRateMain: landUse.mainLandUseCode,
            tripRateSub: landUse.subLandUseCode,
            tripRateSearch: getLandUseOptionLabel(landUse),
        });
    };

    const handleDropDownChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        let update: any = {};

        switch (name) {
            case "selectedType":
                update = area == 'TripRate'
                    ? { tripRateType: value, tripRateMain: "01", tripRateSub: "A", tripRateSearch: "" }
                    : { siteListType: value, siteListMain: value === 'archived' && sessionState.siteListMain !== "" ? "" : "01", siteListSub: value === 'archived' && sessionState.siteListMain !== "" ? "" : "A", siteListLandSearch: "", siteListPage: 0, siteListTableState: { ...sessionState.siteListTableState, siteList: { ...sessionState.siteListTableState.siteList, page: 0 } } };
                break;

            case "selectedMain":
                update = area == 'TripRate'
                    ? { tripRateMain: value, tripRateSub: "A", LandusePage: { start: null, end: null } }
                    : { siteListMain: value, siteListSub: "A", siteListPage: 0, siteListTableState: { ...sessionState.siteListTableState, siteList: { ...sessionState.siteListTableState.siteList, page: 0 } } };
                break;

            case "selectedSub":
                update = area == 'TripRate'
                    ? { tripRateMain: sessionState.tripRateMain, tripRateSub: value, LandusePage: { start: null, end: null } }
                    : { siteListPage: 0, siteListMain:sessionState.siteListMain, siteListSub: value, siteListTableState: { ...sessionState.siteListTableState, siteList: { ...sessionState.siteListTableState.siteList, page: 0 } } };
                break;

            case "selectedFull":
                const [main, sub] = value.split("/");
                update = area == 'TripRate'
                    ? { tripRateMain: main, tripRateSub: sub, LandusePage: { start: null, end: null } }
                    : { siteListMain: main, siteListSub: sub, siteListPage: 0, siteListTableState: { ...sessionState.siteListTableState, siteList: { ...sessionState.siteListTableState.siteList, page: 0 } } };
                break;

            default:
                console.warn(`Invalid dropdown name: ${name}`);
                return;
        }

        updateSessionState({ ...sessionState, ...update }, (newState) => {
            if (getData && value !== 'archived') {
                getData(newState);
            }
        });
    };

    return (
        <>
            {area == 'SiteList' &&
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 13.5fr) minmax(0, 24.5fr)',
                        columnGap: 1,
                        rowGap: 1,
                        width: '38rem',
                        maxWidth: '100%',
                    }}
                >
                    <Box>
                        <TextField
                            label="Select land use by"
                            select
                            value={sessionState.siteListType}
                            name="selectedType"
                            onChange={handleDropDownChange}
                            fullWidth
                            size='small'
                            margin="none"
                            disabled={loading}
                        >
                            <MenuItem key={1} value="active">Active Land Uses</MenuItem>
                            <MenuItem key={2} value="search">Search For Land Use</MenuItem>
                            <MenuItem key={3} value="archived">Land Use Archive</MenuItem>
                        </TextField>
                    </Box>
                    {sessionState.siteListType === 'active' &&
                        <>
                            <Box>
                                <TextField
                                    label="Main Land Use"
                                    select
                                    value={sessionState.siteListMain}
                                    name="selectedMain"
                                    onChange={handleDropDownChange}
                                    fullWidth
                                    size='small'
                                    margin="none"
                                    disabled={loading}
                                >
                                    {Array.from(landUseDropDown
                                        .filter((item) => item.archived === false)
                                        .reduce((map, { mainLandUseCode, mainLandUse }) => {
                                            if (!map.has(mainLandUseCode)) {
                                                map.set(mainLandUseCode, mainLandUse);
                                            }
                                            return map;
                                        }, new Map()))
                                        .map(([key, value]) => (
                                            <MenuItem key={key} value={key}>{`${key} - ${value}`}</MenuItem>
                                        ))
                                    }
                                </TextField>
                            </Box>
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <TextField
                                    label="Sub Land Use"
                                    select
                                    value={sessionState.siteListSub}
                                    name="selectedSub"
                                    onChange={handleDropDownChange}
                                    fullWidth
                                    size='small'
                                    margin="none"
                                    disabled={loading}
                                >
                                    {landUseDropDown
                                        .filter((item) => item.archived === false && item.mainLandUseCode === sessionState.siteListMain)
                                        .map((item) => (
                                            <MenuItem key={item.subLandUseCode} value={item.subLandUseCode}>{`${item.subLandUseCode} - ${item.subLandUse}`}</MenuItem>
                                        ))
                                    }
                                </TextField>
                            </Box>
                        </>
                    }
                    {sessionState.siteListType === 'search' &&
                        <>
                            <Box>
                                <Autocomplete
                                    options={landUseDropDown.filter((item) => item.archived === false)}
                                    value={selectedSiteListLandUse}
                                    inputValue={sessionState.siteListLandSearch}
                                    onInputChange={handleSiteListLandUseSearch}
                                    onChange={handleSiteListLandUseSelect}
                                    clearOnBlur={false}
                                    disableClearable={false}
                                    getOptionLabel={getLandUseOptionLabel}
                                    isOptionEqualToValue={(option, value) =>
                                        option.mainLandUseCode === value.mainLandUseCode &&
                                        option.subLandUseCode === value.subLandUseCode
                                    }
                                    filterOptions={(options) =>
                                        filterLandUseOptions(options, sessionState.siteListLandSearch)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search Land Use"
                                            fullWidth
                                            size="small"
                                            disabled={loading}
                                            margin="none"
                                        />
                                    )}
                                    fullWidth
                                    disabled={loading}
                                />
                            </Box>
                        </>
                    }
                    {sessionState.siteListType === 'archived' &&
                        <>
                            <Box>
                                <TextField
                                    label="Archived Land Use"
                                    select
                                    value={`${sessionState.siteListMain}/${sessionState.siteListSub}`}
                                    name="selectedFull"
                                    onChange={handleDropDownChange}
                                    fullWidth
                                    size='small'
                                    margin="none"
                                    disabled={loading}
                                >
                                    {landUseDropDown
                                        .filter((item) => item.archived === true)
                                        .map((item) => (
                                            <MenuItem key={`${item.mainLandUseCode}/${item.subLandUseCode}`} value={`${item.mainLandUseCode}/${item.subLandUseCode}`}>{`${item.mainLandUseCode}/${item.subLandUseCode} - ${item.subLandUse}`}</MenuItem>
                                        ))
                                    }
                                </TextField>
                            </Box>
                        </>
                    }
                </Box>
            }
            {area == 'TripRate' &&
                <Grid container>
                    <Grid size={4}>
                        <TextField
                            label="Select land use by"
                            select
                            value={sessionState.tripRateType}
                            name="selectedType"
                            onChange={handleDropDownChange}
                            fullWidth
                            size='small'
                            margin="none"
                            disabled={loading}
                        >
                            <MenuItem key={1} value="active">Active Land Uses</MenuItem>
                            <MenuItem key={2} value="search">Search For Land Use</MenuItem>
                            <MenuItem key={3} value="archived">Land Use Archive</MenuItem>
                        </TextField>
                    </Grid>
                    {sessionState.tripRateType === 'active' &&
                        <>
                            <Grid size={8}>
                                <TextField
                                    label="Main Land Use"
                                    select
                                    value={sessionState.tripRateMain}
                                    name="selectedMain"
                                    onChange={handleDropDownChange}
                                    fullWidth
                                    size='small'
                                    margin="none"
                                    disabled={loading}
                                >
                                    {Array.from(landUseDropDown
                                        .filter((item) => item.archived === false)
                                        .reduce((map, { mainLandUseCode, mainLandUse }) => {
                                            if (!map.has(mainLandUseCode)) {
                                                map.set(mainLandUseCode, mainLandUse);
                                            }
                                            return map;
                                        }, new Map()))
                                        .map(([key, value]) => (
                                            <MenuItem key={key} value={key}>{`${key} - ${value}`}</MenuItem>
                                        ))
                                    }
                                </TextField>
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    label="Sub Land Use"
                                    select
                                    value={sessionState.tripRateSub}
                                    name="selectedSub"
                                    onChange={handleDropDownChange}
                                    fullWidth
                                    size='small'
                                    margin="none"
                                    disabled={loading}
                                >
                                    {landUseDropDown
                                        .filter((item) => item.archived === false && item.mainLandUseCode === sessionState.tripRateMain)
                                        .map((item) => (
                                            <MenuItem key={item.subLandUseCode} value={item.subLandUseCode}>{`${item.subLandUseCode} - ${item.subLandUse}`}</MenuItem>
                                        ))
                                    }
                                </TextField>
                            </Grid>
                        </>
                    }
                    {sessionState.tripRateType === 'search' &&
                        <>
                            <Grid size={8}>
                                <Autocomplete
                                    options={landUseDropDown.filter((item) => item.archived === false)}
                                    value={selectedTripRateLandUse}
                                    inputValue={sessionState.tripRateSearch}
                                    onInputChange={handleTripRateLandUseSearch}
                                    onChange={handleTripRateLandUseSelect}
                                    clearOnBlur={false}
                                    disableClearable={false}
                                    getOptionLabel={getLandUseOptionLabel}
                                    isOptionEqualToValue={(option, value) =>
                                        option.mainLandUseCode === value.mainLandUseCode &&
                                        option.subLandUseCode === value.subLandUseCode
                                    }
                                    filterOptions={(options) =>
                                        filterLandUseOptions(options, sessionState.tripRateSearch)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search Land Use"
                                            fullWidth
                                            size="small"
                                            disabled={loading}
                                            margin="none"
                                        />
                                    )}
                                    fullWidth
                                    disabled={loading}
                                />
                            </Grid>
                        </>
                    }
                    {sessionState.tripRateType === 'archived' &&
                        <>
                            <Grid size={8}>
                                <TextField
                                    label="Archived Land Use"
                                    select
                                    value={`${sessionState.tripRateMain}/${sessionState.tripRateSub}`}
                                    name="selectedFull"
                                    onChange={handleDropDownChange}
                                    fullWidth
                                    size='small'
                                    margin="none"
                                    disabled={loading}
                                >
                                    {landUseDropDown
                                        .filter((item) => item.archived === true)
                                        .map((item) => (
                                            <MenuItem key={`${item.mainLandUseCode}/${item.subLandUseCode}`} value={`${item.mainLandUseCode}/${item.subLandUseCode}`}>{`${item.mainLandUseCode}/${item.subLandUseCode} - ${item.subLandUse}`}</MenuItem>
                                        ))
                                    }
                                </TextField>
                            </Grid>
                        </>
                    }
                </Grid>
            }

        </>

    );
}
